import ListPageController, { type StatCardDef } from "./mixins/ListPageController";
import JSONModel from "sap/ui/model/json/JSONModel";
import type { SearchField$LiveChangeEvent, SearchField$SearchEvent } from "sap/m/SearchField";
import type { Select$ChangeEvent } from "sap/m/Select";
import type { Link$PressEvent } from "sap/m/Link";

type StatusKey = "ALL" | "Active" | "Inactive" | "Deprecated";

interface ViewState {
	statusFilter: StatusKey;
	searchQuery: string;
}

// Karty statystyk nad tabelą — ten sam fragment StatCardsRow co Organizations/Dashboard.
const STATS: readonly StatCardDef[] = [
	{ key: "total", labelKey: "productFamilies.stats.total.label", subtitleKey: "productFamilies.stats.total.subtitle", state: "Neutral" },
	{ key: "active", labelKey: "productFamilies.stats.active.label", subtitleKey: "productFamilies.stats.active.subtitle", state: "Good", filter: "Status eq 'Active'" },
	{ key: "inactive", labelKey: "productFamilies.stats.inactive.label", subtitleKey: "productFamilies.stats.inactive.subtitle", state: "Critical", filter: "Status eq 'Inactive'" },
	{ key: "deprecated", labelKey: "productFamilies.stats.deprecated.label", subtitleKey: "productFamilies.stats.deprecated.subtitle", state: "Error", filter: "Status eq 'Deprecated'" }
];

/**
 * @namespace computec.b2b.portal.controller
 */
export default class ProductFamilies extends ListPageController {
	public override onInit(): void {
		const initial: ViewState = { statusFilter: "ALL", searchQuery: "" };
		this.getView().setModel(new JSONModel(initial), "view");
		void this.initStatsCards("ProductFamilies", STATS);
		this.getRouter().getRoute("productFamilies")?.attachPatternMatched(() => { void this.onShow(); });
	}

	private async onShow(): Promise<void> {
		const bundle = await this.getResourceBundle();
		const title = bundle.getText("productFamilies.title") ?? "Product Families";
		this.setPageHeader({ title, subtitle: bundle.getText("productFamilies.subtitle") ?? "" });
		this.applyFilters();
		void this.refreshStatsCards();
	}

	public onSearch(event: SearchField$SearchEvent | SearchField$LiveChangeEvent): void {
		const params = event.getParameters() as { query?: string; newValue?: string };
		const raw = params.query ?? params.newValue ?? "";
		(this.getView().getModel("view") as JSONModel).setProperty("/searchQuery", raw);
		this.applyFilters();
	}

	public onStatusSelect(event: Select$ChangeEvent): void {
		const key = (event.getParameter("selectedItem")?.getKey() ?? "ALL") as StatusKey;
		(this.getView().getModel("view") as JSONModel).setProperty("/statusFilter", key);
		this.applyFilters();
	}

	public onRefresh(): void {
		this.refreshTable("pfTable");
		void this.refreshStatsCards();
	}

	public onOpenFamily(event: Link$PressEvent): void {
		const ctx = event.getSource().getBindingContext("odata");
		if (!ctx) {
			return;
		}
		const id = ctx.getProperty("Id") as string | undefined;
		if (id) {
			this.navTo("productFamilyDetail", { id });
		}
	}

	private applyFilters(): void {
		const state = (this.getView().getModel("view") as JSONModel).getData() as ViewState;
		const parts: string[] = [];

		if (state.searchQuery.trim().length > 0) {
			const qLower = escapeOData(state.searchQuery.trim().toLowerCase());
			parts.push(`(contains(tolower(Name),'${qLower}') or contains(tolower(Code),'${qLower}'))`);
		}
		if (state.statusFilter !== "ALL") {
			parts.push(`Status eq '${state.statusFilter}'`);
		}

		const binding = this.getTableBinding("pfTable");
		binding?.changeParameters({ $filter: parts.length > 0 ? parts.join(" and ") : undefined });
	}
}

function escapeOData(value: string): string {
	return value.replace(/'/g, "''");
}
